
import Card from "@mui/material/Card";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "@maysoft/common-component-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants from "constants/index";
import ProjectService from "services/medkit/project.service";
import ModalAddProjectOrganization from "./ModalAddProjectOrganization";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { setListPathName } from "store/slice/titleRoute.slice";
import { setDataAlert, showLoadingDataTable } from "store/slice/common.slice";
import { IRecordProject, IRecordProjectOrganization } from "commons/interfaces";



interface IDataFilterProjectOrganization {
    projectId?: string;
    searchText?: string;
    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
};

const projectService = new ProjectService();

const ProjectOrganizationScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const dataMapCodenames = useSelector((state: RootState) => state.common.codenames);
    const listPathName = useSelector((state: RootState) => state.titleRoute.listPathName);
    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);

    const [openModalAdd, setOpenModalAdd] = useState(false);
    const [dataRows, setDataRows] = useState<IRecordProjectOrganization[]>([]);
    const [dataFilter, setDataFilter] = useState<IDataFilterProjectOrganization>({});
    const [projectDetail, setProjectDetail] = useState<IRecordProject | undefined>(undefined);

    const pageSize = searchParams.get("size");
    const pageNumber = searchParams.get("page");
    const searchText = searchParams.get("text");
    const projectId = searchParams.get("project");

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(projectId)) {
            (async () => {
                try {
                    const result = await projectService.getDetail(projectId);
                    setProjectDetail(result);
                } catch (error) {

                }
            })();
        }
    }, [projectId]);

    useEffect(() => {
        const size = Number(pageSize || Constants.ROW_PER_PAGE);
        const page = Number(pageNumber || Constants.PAGE_DEFAULT);

        const newReq: IDataFilterProjectOrganization = {
            pageSize: size,
            pageNumber: page,
            projectId: projectId,
            searchText: searchText,
        };

        loadDataProjectOrganization(newReq);
    }, [
        pageSize,
        projectId,
        pageNumber,
        searchText,
    ]);

    const loadDataProjectOrganization = async (newReq: IDataFilterProjectOrganization) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await projectService.getPagedProjectOrganization({
                pageSize: newReq.pageSize,
                pageNumber: newReq.pageNumber,
                searchText: newReq.searchText,
                projectId: newReq.projectId,
            });

            setDataFilter(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount || 0 }));

            setDataRows([...result?.items || []]);

        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleFilterData = (req: IDataFilterProjectOrganization) => {
        let query: string = `?project=${req.projectId}`;

        query += `&page=${req.pageNumber || Constants.PAGE_DEFAULT}&size=${req.pageSize || Constants.ROW_PER_PAGE}`;

        if (!Helpers.isNullOrEmpty(req.searchText)) { query += `&text=${req.searchText}`; };

        navigate(Screens.PROJECT_ORGANIZATION + query, { replace: true });

        dispatch(setListPathName({ pathname: Screens.PROJECT_ORGANIZATION, query }));
    };

    const handleDelete = (organizationId: string) => {
        Helpers.showConfirmAlert(
            Strings.PROJECT_MANAGEMENT.CONFIRM_DELETE_PROJECT_ORGANIZATION,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await projectService.deleteProjectOrganization({
                        projectId: dataFilter?.projectId,
                        organizationId: organizationId,
                    });

                    await loadDataProjectOrganization({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.PROJECT_MANAGEMENT.DELETE_PROJECT_ORGANIZATION_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { },
        );
    };

    const handleGoBack = () => {
        const itemP = listPathName.find((el) => el.pathName === Screens.PROJECT);
        navigate(Screens.PROJECT + (itemP ? itemP.query : ""));
    };

    const dataColumns = useMemo(() => ([
        { Header: Strings.PROJECT_MANAGEMENT.ORGANIZATION_NAME, accessor: "organizationName", width: "220px" },
        {
            Header: Strings.PROJECT_MANAGEMENT.ORGANIZATION_TYPE, accessor: "organizationType",
            Cell: ({ value }: any) => {
                return <>{[...dataMapCodenames?.organizationType || []].find(e => `${e.code}` === `${value}`)?.name}</>
            },
        },
    ]), [dataMapCodenames?.organizationType]);

    return (
        <DashboardLayout
            isPermission
            allowSettingLayout
            requiredOrganization
            title={Strings.PROJECT_MANAGEMENT.LIST_ORGANIZATION_OF_PROJECT}
            route={[
                { title: Strings.PROJECT_MANAGEMENT.TITLE, route: Screens.PROJECT },
                { title: Strings.PROJECT_MANAGEMENT.LIST_ORGANIZATION_OF_PROJECT, route: "" },
            ]}
            listActions={[
                {
                    color: "secondary",
                    title: Strings.COMMON.BACK,
                    onPress: handleGoBack,
                },
                {
                    title: Strings.COMMON.ADD_NEW,
                    onPress: () => { setOpenModalAdd(true); },
                }
            ]}
        >
            <Card sx={{ padding: 2 }}>
                <DataTable
                    isShowIndex
                    loading={loadingDataTable}
                    totalCount={dataFilter?.totalCount || 0}
                    rowPerPage={dataFilter?.pageSize || Constants.ROW_PER_PAGE}
                    pageNumber={dataFilter?.pageNumber || Constants.PAGE_DEFAULT}
                    onChangePageSize={(value) => {
                        handleFilterData({ ...dataFilter, pageSize: value, });
                    }}
                    onChangePageNumber={(value) => {
                        handleFilterData({ ...dataFilter, pageNumber: value, });
                    }}
                    table={{ columns: dataColumns as any, rows: dataRows }}

                    searchText={dataFilter?.searchText}
                    placeholderSearch={Strings.PROJECT_MANAGEMENT.PLACEHOLDER_SEARCH_TEXT_BY_ORGANIZATION}
                    onSearch={(value) => {
                        handleFilterData({
                            ...dataFilter,
                            searchText: value,
                            pageNumber: Constants.PAGE_DEFAULT,
                        });
                    }}
                    onSearchChange={(value) => {
                        setDataFilter(prev => ({
                            ...prev,
                            searchText: value,
                            pageNumber: Constants.PAGE_DEFAULT,
                        }));
                    }}
                    actionList={(row) => ([
                        {
                            key: "delete",
                            iconName: "delete",
                            title: Strings.COMMON.DELETE,
                            callback: (row) => {
                                handleDelete(row.organizationId);
                            },
                        },
                    ])}
                />
            </Card>

            {openModalAdd &&
                <ModalAddProjectOrganization
                    projectId={projectDetail?.id}
                    projectName={projectDetail?.name}
                    visible={openModalAdd}
                    setVisible={setOpenModalAdd}
                    onCallBack={() => { loadDataProjectOrganization({ ...dataFilter }); }}
                />
            }
        </DashboardLayout>
    );
};

export default ProjectOrganizationScreen;

